<template>
  <div class="rf-page">
    <!-- 标题栏 -->
    <div class="rf-header">
      <div class="rf-header-left">
        <div class="rf-title">
          <a-icon type="file-word" class="rf-title-icon" />报名表上传
        </div>
        <div class="rf-sub">上传 Word 格式报名表，上传成功后可在线预览内容</div>
      </div>
      <div class="rf-header-right">
        <a-button
          class="rf-tpl-btn"
          :loading="tplLoading"
          @click="downloadTeamForm"
        >
          <a-icon type="download" />下载模板
        </a-button>
        <a-tag v-if="saved" class="rf-saved-tag">
          <a-icon type="check-circle" />已保存
        </a-tag>
        <a-tag class="rf-tag">仅支持 .doc / .docx</a-tag>
      </div>
    </div>

    <div class="rf-body">
      <!-- 左侧：上传面板 -->
      <div class="rf-upload-panel">
        <a-upload-dragger
          name="file"
          :multiple="false"
          accept=".doc,.docx"
          :show-upload-list="false"
          :before-upload="beforeUpload"
          :disabled="uploading"
        >
          <div class="rf-drop-inner">
            <a-icon type="cloud-upload" class="rf-drop-icon" />
            <p class="rf-drop-title">
              <span class="rf-req-flag">*</span>点击或拖拽 Word 报名表到此处上传
              <em class="rf-req-tag">必填</em>
            </p>
            <p class="rf-drop-tip">支持 .doc / .docx 格式，仅可上传一个文件</p>
          </div>
        </a-upload-dragger>

        <!-- 已选文件卡片 -->
        <div class="rf-file-card" v-if="file">
          <div class="rf-file-ico"><a-icon type="file-word" /></div>
          <div class="rf-file-info">
            <div class="rf-file-name" :title="file.name">{{ file.name }}</div>
            <!-- <div class="rf-file-meta">
              {{ fileMetaText || formatSize(file.size) }}
              <template v-if="uploadTime"> · {{ uploadTime }}</template>
            </div>
            <div class="rf-file-status">
              <a-badge :status="statusBadge" :text="statusText" />
            </div> -->
          </div>
          <!-- <div class="rf-file-actions">
            <a-button type="link" @click="reChoose">重新选择</a-button>
            <a-button type="link" v-if="pdfUrl" @click="download">下载</a-button>
          </div> -->
        </div>

        <a-progress
          v-if="uploading"
          :percent="uploadPercent"
          :stroke-color="'#9A0B00'"
          class="rf-progress"
        />

        <!-- 盖章图片上传区 -->
        <a-upload-dragger
          name="file"
          class="rf-photo-dragger"
          :multiple="false"
          accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
          :show-upload-list="false"
          :before-upload="beforePhotoUpload"
          :disabled="photoUploading"
        >
          <div class="rf-drop-inner">
            <a-icon type="file-image" class="rf-drop-icon" />
            <p class="rf-drop-title">
              点击或拖拽盖章图片到此处上传
              <em class="rf-opt-tag">选填</em>
            </p>
            <p class="rf-drop-tip">报名表需加盖公章后拍照/扫描上传，支持 jpg / png 等图片格式，仅可上传一张</p>
          </div>
        </a-upload-dragger>

        <!-- 已选图片卡片（点击可预览） -->
        <div class="rf-photo-card" v-if="photoUrl">
          <img
            class="rf-photo-thumb"
            :src="photoUrl"
            alt="盖章图片"
            @click="openPhotoPreview"
          />
          <div class="rf-photo-info">
            <div class="rf-photo-name" :title="photoName">{{ photoName || '盖章图片' }}</div>
            <div class="rf-photo-hint">请确认已加盖公章，点击图片可放大预览</div>
          </div>
        </div>

        <a-progress
          v-if="photoUploading"
          :percent="photoUploadPercent"
          :stroke-color="'#9A0B00'"
          class="rf-progress"
        />

        <!-- 保存按钮：Word 与盖章图片都上传后点击提交（不挂 type=primary，避免红底红字） -->
        <a-button
          class="rf-save-btn rf-save-panel"
          :loading="saving"
          @click="handleSave"
        >
          <a-icon type="save" />保存
        </a-button>

        <div class="rf-tips">
          <a-icon type="info-circle" />
          提示：Word 报名表为必填项（上传后系统自动转换为 PDF 预览）；盖章图片为选填项，不传也可保存。请上传后点击下方「保存」按钮完成提交。
        </div>
      </div>

      <!-- 右侧：预览面板 -->
      <div class="rf-preview-panel">
        <div class="rf-preview-bar">
          <span class="rf-preview-title"><a-icon type="eye" /> 预览</span>
          <span class="rf-preview-actions" v-if="pdfUrl">
            <span class="rf-preview-type">PDF 文件</span>
            <a-button
              v-if="!previewFail"
              type="primary"
              ghost
              size="small"
              @click="openFullscreen"
            >
              <a-icon type="fullscreen" />全屏查看
            </a-button>
            <a-button v-else type="primary" ghost size="small" @click="openPdfNewTab">
              <a-icon type="export" />新标签页查看
            </a-button>
          </span>
        </div>
        <div class="rf-preview-scroll">
          <!-- 空态 -->
          <div class="rf-empty" v-if="!file">
            <a-icon type="inbox" class="rf-empty-icon" />
            <p>请上传 Word 报名表以预览内容</p>
          </div>
          <!-- 转换中 -->
          <div class="rf-unsupported" v-else-if="!pdfUrl && !uploadError">
            <a-icon type="loading" class="rf-unsupported-icon rf-loading" />
            <p>文件上传中，转换完成后将自动展示预览…</p>
          </div>
          <!-- 上传失败 -->
          <div class="rf-unsupported" v-else-if="uploadError">
            <a-icon type="file-exclamation" class="rf-unsupported-icon" />
            <p>上传失败，请重新选择文件再试一次。</p>
          </div>
          <!-- PDF 预览（blob 本地地址，规避 iframe 内嵌限制） -->
          <div class="rf-pdf-wrap" v-else-if="pdfUrl && previewSrc">
            <iframe
              :src="previewSrc"
              class="rf-pdf-frame"
              frameborder="0"
            ></iframe>
          </div>
          <!-- PDF 拉取/后台转换中 -->
          <div class="rf-unsupported" v-else-if="pdfUrl && !previewSrc && !previewFail">
            <a-icon type="loading" class="rf-unsupported-icon rf-loading" />
            <p>PDF 正在生成/加载中，约需几秒，请稍候…</p>
          </div>
          <!-- PDF 内嵌预览被拦截 -->
          <div class="rf-unsupported" v-else-if="pdfUrl && previewFail">
            <a-icon type="file-exclamation" class="rf-unsupported-icon" />
            <p>当前浏览器禁止在页面内嵌预览该 PDF，请点击右上角「新标签页查看」。</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 全屏预览 -->
    <div v-if="fullscreenVisible" class="rf-fullscreen" @click.self="closeFullscreen">
      <div class="rf-fullscreen-header">
        <span class="rf-fullscreen-title"><a-icon type="file-pdf" /> 报名表预览</span>
        <a-button type="primary" ghost @click="closeFullscreen">
          <a-icon type="close" />关闭
        </a-button>
      </div>
      <iframe
        v-if="previewSrc"
        :src="previewSrc"
        class="rf-fullscreen-frame"
        frameborder="0"
      ></iframe>
    </div>

    <!-- 图片预览（盖章图片放大查看）：当前页面内 antdv 弹窗，不新开窗口 -->
    <a-modal
      :visible="photoPreviewVisible"
      :footer="null"
      :width="'80%'"
      title="盖章图片预览"
      :dialog-style="{ top: '8vh' }"
      wrap-class-name="rf-img-modal-wrap"
      @cancel="photoPreviewVisible = false"
    >
      <div class="rf-img-modal-bar">
        <a-button size="small" @click="photoZoomOut">
          <a-icon type="zoom-out" />缩小
        </a-button>
        <span class="rf-img-modal-scale">{{ Math.round(photoZoom * 100) }}%</span>
        <a-button size="small" @click="photoZoomIn">
          <a-icon type="zoom-in" />放大
        </a-button>
        <a-button size="small" @click="photoZoomReset">重置</a-button>
        <span class="rf-img-modal-tip">支持滚轮缩放，按住图片可拖拽平移</span>
      </div>
      <div
        class="rf-img-modal-body"
        @wheel.prevent="photoWheel"
        @mousemove="photoDragMove"
        @mouseup="photoDragEnd"
        @mouseleave="photoDragEnd"
      >
        <img
          :src="photoUrl"
          class="rf-img-modal-img"
          :style="photoImgStyle"
          alt="盖章图片"
          draggable="false"
          @mousedown.prevent="photoDragStart"
          @dblclick="photoDragReset"
        />
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  fileUps,
  lxDrawFileAdd,
  lxDrawFileEdit,
  lxDrawFileList,
  lxDrawTeamDownloadTeamForm,
} from "@/api/index";
import { baseURL } from "@/utils/request";

export default {
  name: "RegistrationForm",
  data() {
    return {
      file: null, // 选中的 File 对象（历史记录回显时为 { name } 伪对象）
      fileMetaText: "", // 文件大小展示文案（上传接口/历史记录直接带 size 时用）
      pdfPath: "", // 预览 PDF 路径（后端返回原始值）
      previewSrc: "", // 实际用于 iframe 的地址（优先 blob 本地地址，规避浏览器内嵌限制）
      previewFail: false, // PDF 拉取失败/被拦截标记
      wordResult: null, // Word 上传后接口返回的完整结果（用于保存时拼装字段）
      branchId: "", // 登录人 id（分社/学校账号）
      uploading: false,
      uploadPercent: 0,
      uploadStatus: "wait", // wait | uploading | done | error
      uploadTime: "",
      uploadError: false,
      fullscreenVisible: false,
      tplLoading: false, // 下载模板按钮 loading
      saving: false, // 保存按钮 loading
      saved: false, // 是否已保存（回显/刚保存后置 true）
      currentRecord: null, // 当前分社已有的报名表记录（来自 list），有则更新，无则新增
      // 盖章图片相关
      photoPath: "", // 图片在后端存储的相对路径（保存参数值 photoPath）
      photoName: "", // 图片文件名
      photoLocalUrl: "", // 图片本地预览地址（blob，规避跨域内嵌限制）
      photoUploading: false,
      photoUploadPercent: 0,
      photoPreviewVisible: false, // 图片放大预览弹层
      photoZoom: 1, // 图片预览缩放比例（1=原始大小）
      photoOffsetX: 0, // 拖拽平移偏移 X（屏幕像素）
      photoOffsetY: 0, // 拖拽平移偏移 Y（屏幕像素）
      photoDragging: false, // 是否正在拖拽
      photoDragX: 0, // 拖拽起始鼠标 X
      photoDragY: 0, // 拖拽起始鼠标 Y
    };
  },
  computed: {
    statusBadge() {
      const map = {
        wait: "default",
        uploading: "processing",
        done: "success",
        error: "error",
      };
      return map[this.uploadStatus] || "default";
    },
    statusText() {
      const map = {
        wait: "待上传",
        uploading: "上传中…",
        done: "已完成",
        error: "上传失败",
      };
      return map[this.uploadStatus] || "";
    },
    // PDF 完整预览地址
    pdfUrl() {
      if (!this.pdfPath) return "";
      const raw = String(this.pdfPath).trim();
      if (/^https?:\/\//i.test(raw)) return raw;
      // 兼容带/不带前导斜杠、带/不带 jeecg-boot 前缀的相对路径
      const rel = raw.replace(/^\/+/, "").replace(/^jeecg-boot\/+/, "");
      return `${baseURL}${rel}`;
    },
    // 盖章图片完整预览地址：本地 blob 优先，回显时拼 baseURL
    photoUrl() {
      if (this.photoLocalUrl) return this.photoLocalUrl;
      if (!this.photoPath) return "";
      const raw = String(this.photoPath).trim();
      if (/^https?:\/\//i.test(raw)) return raw;
      const rel = raw.replace(/^\/+/, "").replace(/^jeecg-boot\/+/, "");
      return `${baseURL}${rel}`;
    },
    // 是否可保存：已上传 Word 或已有历史记录即可（图片可选）
    canSave() {
      return !!(this.wordResult || this.currentRecord);
    },
    // 图片预览：缩放 + 平移的合并样式（拖拽中去掉过渡，跟手更顺）
    photoImgStyle() {
      return {
        transform:
          "translate(" +
          this.photoOffsetX +
          "px, " +
          this.photoOffsetY +
          "px) scale(" +
          this.photoZoom +
          ")",
        transition: this.photoDragging ? "none" : "transform 0.12s ease-out",
        cursor: this.photoDragging ? "grabbing" : "grab",
      };
    },
  },
  beforeDestroy() {
    this.revokePreview();
    this.revokePhoto();
  },
  mounted() {
    // 登录人 id 作为 branchId
    const uid = this.$store.state.user && this.$store.state.user.id;
    if (uid) {
      this.branchId = uid;
      this.loadRecord();
    }
  },
  methods: {
    // 下载报名表模板（lx/lxDrawTeam/downloadTeamForm，接口返回 JSON：含文件相对路径）
    downloadTeamForm() {
      if (this.tplLoading) return;
      this.tplLoading = true;
      lxDrawTeamDownloadTeamForm({})
        .then((res) => {
          // 500/未登录时，request.js 拦截器最后一行 Promise.reject(error) 缺 return，
          // 会把异常 resolve 成 undefined，这里兜住
          if (!res) {
            this.$message.error("下载失败，请确认已登录且文件存在");
            return;
          }
          if (res.code !== 200 || !res.result) {
            this.$message.error(res.message || "模板路径获取失败");
            return;
          }
          // res.result 形如 sys/common/static/template/参赛团队报名表.docx
          // 拼成完整可访问 URL（兼容带/不带前导斜杠、带/不带 jeecg-boot 前缀的相对路径）
          const rel = String(res.result)
            .trim()
            .replace(/^\/+/, "")
            .replace(/^jeecg-boot\/+/, "");
          const url = baseURL + rel;
          const a = document.createElement("a");
          a.href = url;
          // 同源 download 才生效，docx 浏览器不识别会直接下载
          a.download = "参赛团队报名表.docx";
          // Firefox 要求 a 在 DOM 中才会触发下载
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch(() => {
          this.$message.error("下载失败，请确认已登录且文件存在");
        })
        .finally(() => {
          this.tplLoading = false;
        });
    },
    beforeUpload(file) {
      const isWord = /\.(doc|docx)$/i.test(file.name);
      if (!isWord) {
        this.$message.error("仅支持 Word 文件（.doc / .docx）");
        return false;
      }
      // 重置预览相关状态
      this.fullscreenVisible = false;
      this.revokePreview();
      this.previewFail = false;
      this.file = file;
      this.fileMetaText = "";
      this.pdfPath = "";
      this.wordResult = null; // 新上传的 Word 会重新赋值
      this.uploadError = false;
      this.uploadStatus = "uploading";
      this.uploadTime = this.formatTime(new Date());
      this.uploadPercent = 0;
      this.saved = false; // 重新选择后需再次保存
      this.uploadFile(file);
      return false; // 阻止 antdv 自动上传，改为手动控制
    },
    uploadFile(file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("biz", "linxuan");
      this.uploading = true;
      fileUps(formData, (percent) => {
        this.uploadPercent = percent;
      })
        .then((res) => {
          this.uploading = false;
          if (res.code === 200) {
            // 后端转换后返回 PDF 路径（优先 previewPath）
            const result = res.result || {};
            const preview =
              result.previewPath || result.pdfPath || result.pdf || "";
            if (preview) {
              // 后端转 PDF 需要几秒~十几秒，给足重试窗口（每2s一次，最多约20s）等待就绪
              this.setPdfPreview(preview, 10, 2000);
              this.uploadStatus = "done";
              this.fileMetaText = result.size || "";
              // 暂存上传结果，保存由「保存」按钮触发（落库时一并带上 photoPath）
              this.wordResult = result;
              this.saved = false;
              // this.$message.success("上传成功，已生成预览");
              this.$message.success("Word 报名表上传成功，请点击下方「保存」按钮完成提交");
            } else {
              this.uploadStatus = "error";
              this.uploadError = true;
              this.$message.error("上传成功但未获取到 PDF 地址");
            }
          } else {
            this.uploadStatus = "error";
            this.uploadError = true;
            this.$message.error("上传失败：" + (res.message || ""));
          }
        })
        .catch(() => {
          this.uploading = false;
          this.uploadStatus = "error";
          this.uploadError = true;
          this.$message.error("上传失败，请稍后重试");
        });
    },
    // 查询当前登录人的报名表记录（lx/lxDrawFile/list 带 branchId）
    // silent=true：只刷新 currentRecord，不重设预览/文件卡片，
    // 避免打断上传后正在进行中的 PDF 拉取重试链
    loadRecord(silent) {
      lxDrawFileList({ branchId: this.branchId })
        .then((res) => {
          if (res.code === 200 && res.result) {
            // list 接口返回分页结构 { pages, records: [...] }，取第 0 条
            const result = res.result || {};
            const list = Array.isArray(result)
              ? result
              : Array.isArray(result.records)
              ? result.records
              : [result];
            const rec = list[0];
            // 无记录：保持 currentRecord=null（上传时走 add）
            if (!rec || !(rec.previewPath || rec.filePath)) {
              if (!silent) this.currentRecord = null;
              return;
            }
            // 有记录：缓存起来（上传时走 edit，需带 id）
            this.currentRecord = rec;
            if (silent) return; // 静默刷新：仅更新记录，下次上传即可走 edit
            // 回显已有文件：预览 + 文件卡片
            const preview = rec.previewPath || rec.pdfPath || rec.filePath || "";
            this.setPdfPreview(preview);
            const fileName = (rec.filePath || "").split("/").pop();
            this.file = { name: rec.title || fileName || "历史报名表" };
            this.fileMetaText =
              rec.size ||
              (rec.fileSize ? this.formatSize(Number(rec.fileSize)) : "");
            this.uploadStatus = "done";
            // 回显盖章图片
            this.photoPath = rec.photoPath || "";
            this.photoName = (rec.photoPath || "").split("/").pop();
            this.photoLocalUrl = "";
            this.saved = true; // 历史记录视为已保存，再次保存走 edit
            this.$forceUpdate();
          } else if (!silent) {
            this.currentRecord = null;
          }
        })
        .catch((e) => {
          // 无记录/异常不阻塞页面，保持空态即可
          // 静默刷新失败时保留原有 currentRecord，避免已保存的记录被清空又走 add
          if (!silent) this.currentRecord = null;
          console.error("报名表详情查询失败", e);
        });
    },
    // 点击「保存」按钮：把 Word 报名表信息与盖章图片（photoPath）一并落库
    handleSave() {
      if (this.saving) return;
      if (!this.canSave) {
        this.$message.warning("请先上传 Word 报名表");
        return;
      }
      this.saving = true;
      // 组装保存参数（Word 字段来自新上传结果或历史记录，图片字段来自 photoPath）
      const word = this.wordResult || {};
      const rec = this.currentRecord || {};
      const recVal = (k) =>
        rec && rec[k] != null && rec[k] !== "" ? rec[k] : "";
      const payload = {
        branchId: this.branchId,
        filePath: word.path || word.filePath || recVal("filePath") || "",
        previewPath:
          word.previewPath ||
          word.pdfPath ||
          recVal("previewPath") ||
          recVal("pdfPath") ||
          "",
        coverImgPath:
          word.coverImgPath ||
          word.coverImgpath ||
          word.coverImg ||
          recVal("coverImgPath") ||
          "",
        resType: word.resType || "WORD",
        title: word.title || recVal("title") || "",
        size: word.size || recVal("size") || "",
        // 新增：盖章图片路径
        photoPath: this.photoPath || recVal("photoPath") || "",
      };
      // 数值/可为空的字段，有值才带上
      if (word.fileSize != null) payload.fileSize = word.fileSize;
      if (word.format != null) payload.format = word.format;
      if (word.totalTime != null) payload.totalTime = word.totalTime;
      if (word.zyresId != null) payload.zyresId = word.zyresId;

      const isUpdate = !!(rec && rec.id);
      // 更新：带 id 走 edit；新增：不带 id 走 add
      const request = isUpdate
        ? lxDrawFileEdit({ id: rec.id, ...payload })
        : lxDrawFileAdd(payload);

      request
        .then((res) => {
          this.saving = false;
          if (res.code === 200) {
            // 先用接口返回做本地兜底缓存（后端未回带记录时也能保持"已有记录"状态）
            const saved = res.result;
            if (saved && saved.id) {
              this.currentRecord = saved;
            } else if (isUpdate) {
              // 即使后端没回 id，也保持有记录的标志（避免下次又走 add）
              this.currentRecord = Object.assign({}, rec, payload);
            }
            this.saved = true;
            this.$message.success("保存成功");
            // 再回查一次 list 拿后端权威记录（含 id），
            // 否则首次 add 拿不到 id，下次上传仍会走 add 导致重复新增
            this.loadRecord(true);
          } else {
            this.$message.error(
              (isUpdate ? "文件信息更新失败：" : "文件信息保存失败：") +
                (res.message || "")
            );
          }
        })
        .catch(() => {
          this.saving = false;
          this.$message.error(
            (isUpdate ? "文件信息更新失败" : "文件信息保存失败") + "，请稍后重试"
          );
        });
    },
    // 上传成功后落库（兼容旧调用）：直接转交 handleSave
    saveRecord() {
      this.handleSave();
    },
    // 设置预览路径并触发 blob 拉取（retryLeft/delayMs 传给 fetchPdfBlob 控制失败重试）
    setPdfPreview(rawPath, retryLeft = 3, delayMs = 2000) {
      this.revokePreview();
      this.pdfPath = (rawPath || "").trim();
      this.previewFail = false;
      if (this.pdfPath) {
        this.$nextTick(() => this.fetchPdfBlob(retryLeft, delayMs));
      }
    },
    // 用 fetch 把 PDF 拉成 blob，再以本地 objectURL 预览，
    // 可规避浏览器内置 PDF 查看器对 iframe 内嵌/跨域/防嵌套头的限制
    // retryLeft：失败自动重试次数（后端转换 PDF 需要时间，首次往往拉不到）；delayMs：重试间隔
    async fetchPdfBlob(retryLeft = 3, delayMs = 2000) {
      const url = this.pdfUrl;
      if (!url) return;
      try {
        const resp = await fetch(url, {
          credentials: "include",
          cache: "no-cache",
        });
        if (!resp.ok) throw new Error("HTTP " + resp.status);
        const blob = await resp.blob();
        if (!(await this.isPdfBlob(blob))) throw new Error("NOT_PDF");
        if (!this.pdfPath) return; // 期间已被重置/重新选择
        this.revokePreview();
        this.previewSrc = URL.createObjectURL(blob);
        this.previewFail = false;
      } catch (e) {
        // 拉取失败：多为后端 PDF 尚在转换/未就绪，延迟后自动重试
        if (!this.pdfPath) return;
        if (retryLeft > 0) {
          this._pdfRetryTimer = setTimeout(() => {
            this._pdfRetryTimer = null;
            if (this.pdfPath) this.fetchPdfBlob(retryLeft - 1, delayMs);
          }, delayMs);
        } else {
          this.previewFail = true; // 重试耗尽仍失败：提示新标签页查看
        }
      }
    },
    // 校验 blob 是否为真正的 PDF（前 5 字节 %PDF-）
    isPdfBlob(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arr = new Uint8Array(reader.result);
          resolve(
            arr.length >= 5 &&
              arr[0] === 37 && // %
              arr[1] === 80 && // P
              arr[2] === 68 && // D
              arr[3] === 70 && // F
              arr[4] === 45 // -
            );
        };
        reader.onerror = () => resolve(false);
        reader.readAsArrayBuffer(blob.slice(0, 5));
      });
    },
    revokePreview() {
      // 先清掉未触发的重试定时器，避免切换到新文件后旧重试又执行
      if (this._pdfRetryTimer) {
        clearTimeout(this._pdfRetryTimer);
        this._pdfRetryTimer = null;
      }
      if (this.previewSrc) {
        URL.revokeObjectURL(this.previewSrc);
        this.previewSrc = "";
      }
    },
    revokePhoto() {
      if (this.photoLocalUrl) {
        URL.revokeObjectURL(this.photoLocalUrl);
        this.photoLocalUrl = "";
      }
    },
    reChoose() {
      this.fullscreenVisible = false;
      this.revokePreview();
      this.previewFail = false;
      this.file = null;
      this.pdfPath = "";
      this.wordResult = null;
      this.uploading = false;
      this.uploadPercent = 0;
      this.uploadStatus = "wait";
      this.uploadError = false;
      this.saved = false;
    },
    // 盖章图片：选择后上传到服务器，得到后端路径 photoPath（本地用 blob 预览，规避跨域）
    beforePhotoUpload(file) {
      const isImg = /\.(jpe?g|png|gif|bmp|webp)$/i.test(file.name);
      if (!isImg) {
        this.$message.error("仅支持图片文件（jpg / png / gif 等）");
        return false;
      }
      this.photoUploading = true;
      this.photoUploadPercent = 0;
      this.photoPreviewVisible = false;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("biz", "linxuan");
      fileUps(formData, (percent) => {
        this.photoUploadPercent = percent;
      })
        .then((res) => {
          this.photoUploading = false;
          if (res.code === 200) {
            const result = res.result || {};
            // 图片在后端存储的相对路径，作为保存参数 photoPath
            const path =
              result.path ||
              result.filePath ||
              result.coverImgPath ||
              result.url ||
              "";
            this.photoPath = path;
            this.photoName = file.name;
            this.revokePhoto();
            this.photoLocalUrl = URL.createObjectURL(file);
            this.saved = false; // 图片变化后需重新保存
            this.$message.success("图片上传成功，请点击「保存」按钮完成提交");
          } else {
            this.$message.error("图片上传失败：" + (res.message || ""));
          }
        })
        .catch(() => {
          this.photoUploading = false;
          this.$message.error("图片上传失败，请稍后重试");
        });
      return false; // 阻止 antdv 自动上传，改为手动控制
    },
    // 重新选择盖章图片：清除已上传的图片状态
    reChoosePhoto() {
      this.photoPreviewVisible = false;
      this.revokePhoto();
      this.photoPath = "";
      this.photoName = "";
      this.photoUploading = false;
      this.photoUploadPercent = 0;
      this.saved = false;
    },
    // 打开图片预览并重置缩放与平移
    openPhotoPreview() {
      this.photoZoom = 1;
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
      this.photoDragging = false;
      this.photoPreviewVisible = true;
    },
    // 缩放：放大 / 缩小 / 重置
    photoZoomIn() {
      this.photoZoom = Math.min(+(this.photoZoom + 0.2).toFixed(2), 4);
    },
    photoZoomOut() {
      this.photoZoom = Math.max(+(this.photoZoom - 0.2).toFixed(2), 0.4);
    },
    photoZoomReset() {
      this.photoZoom = 1;
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
    },
    // 滚轮缩放（向上放大、向下缩小）
    photoWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.photoZoom = Math.min(
        4,
        Math.max(0.4, +(this.photoZoom + delta).toFixed(2))
      );
    },
    // 拖拽平移：按下记录起点
    photoDragStart(e) {
      this.photoDragging = true;
      this.photoDragX = e.clientX;
      this.photoDragY = e.clientY;
    },
    // 拖拽平移：随鼠标移动偏移图片
    photoDragMove(e) {
      if (!this.photoDragging) return;
      const dx = e.clientX - this.photoDragX;
      const dy = e.clientY - this.photoDragY;
      this.photoOffsetX += dx;
      this.photoOffsetY += dy;
      this.photoDragX = e.clientX;
      this.photoDragY = e.clientY;
    },
    // 拖拽平移：松开结束
    photoDragEnd() {
      this.photoDragging = false;
    },
    // 双击图片：平移归位（缩放不变）
    photoDragReset() {
      this.photoOffsetX = 0;
      this.photoOffsetY = 0;
    },
    download() {
      if (!this.pdfUrl) {
        this.$message.warning("暂无下载地址");
        return;
      }
      window.open(this.pdfUrl);
    },
    openPdfNewTab() {
      if (!this.pdfUrl) {
        this.$message.warning("暂无预览地址");
        return;
      }
      window.open(this.pdfUrl, "_blank");
    },
    openFullscreen() {
      if (!this.pdfUrl) {
        this.$message.warning("暂无预览地址");
        return;
      }
      // blob 拉取成功：弹层内嵌预览；失败：直接新标签页打开
      if (!this.previewSrc || this.previewFail) {
        window.open(this.pdfUrl, "_blank");
        return;
      }
      this.fullscreenVisible = true;
    },
    closeFullscreen() {
      this.fullscreenVisible = false;
    },
    formatSize(bytes) {
      if (!bytes && bytes !== 0) return "-";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return (bytes / Math.pow(k, i)).toFixed(2) + " " + sizes[i];
    },
    formatTime(d) {
      const p = (n) => (n < 10 ? "0" + n : "" + n);
      return (
        d.getFullYear() +
        "-" +
        p(d.getMonth() + 1) +
        "-" +
        p(d.getDate()) +
        " " +
        p(d.getHours()) +
        ":" +
        p(d.getMinutes())
      );
    },
  },
};
</script>

<style scoped lang="scss">
.rf-page {
  padding: 20px 25px 30px;
  height: 100%;
  min-height: 600px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.rf-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(90deg, #9a0b00 0%, #c0392b 100%);
  border-radius: 10px;
  padding: 18px 26px;
  color: #fff;
  box-shadow: 0 4px 14px rgba(154, 11, 0, 0.25);
}
.rf-title {
  font-size: 22px;
  font-weight: bold;
  font-family: Microsoft YaHei;
  display: flex;
  align-items: center;
}
.rf-title-icon {
  margin-right: 10px;
  font-size: 24px;
  color: #ffd27a;
}
.rf-sub {
  margin-top: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
}
.rf-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.rf-tpl-btn {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  font-size: 13px;
  border-radius: 6px;
  height: 32px;
  padding: 0 14px;
}
// .rf-tpl-btn:hover,
// .rf-tpl-btn:focus {
//   color: #9a0b00;
//   background: #ffd27a;
//   border-color: #ffd27a;
// }
.rf-tag {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #fff;
  font-size: 13px;
}
/* 保存按钮：金底深红字，完全自定义（不依赖 antdv primary，避免红底红字） */
.rf-save-btn {
  background: #ffd27a !important;
  border-color: #ffb020 !important;
  color: #9a0b00 !important;
  font-weight: bold;
  font-size: 15px;
  border-radius: 6px;
  height: 40px;
  padding: 0 18px;
  box-shadow: 0 2px 6px rgba(154, 11, 0, 0.18);
}
.rf-save-btn:hover,
.rf-save-btn:focus,
.rf-save-btn:active {
  background: #ffe0a0 !important;
  border-color: #ffb020 !important;
  color: #9a0b00 !important;
}
/* 上传面板内的保存按钮：占满宽度、更醒目 */
.rf-save-panel {
  margin-top: 18px;
  width: 100%;
}
.rf-saved-tag {
  background: rgba(76, 175, 80, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  font-size: 13px;
}
.rf-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  margin-top: 18px;
  gap: 18px;
  align-items: stretch;
}
/* 左侧上传 */
.rf-upload-panel {
  width: 360px;
  flex: 0 0 360px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}
.rf-drop-inner {
  padding: 34px 16px;
  cursor: pointer;
}
.rf-drop-icon {
  font-size: 46px;
  color: #9a0b00;
}
.rf-drop-title {
  margin-top: 12px;
  font-size: 15px;
  font-weight: bold;
  color: #333;
}
/* 必填 / 选填 标记 */
.rf-req-flag {
  color: #e02020;
  margin-right: 2px;
}
.rf-req-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  font-size: 11px;
  font-style: normal;
  font-weight: normal;
  line-height: 18px;
  color: #fff;
  background: #e02020;
  border-radius: 3px;
  vertical-align: middle;
}
.rf-opt-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  font-size: 11px;
  font-style: normal;
  font-weight: normal;
  line-height: 18px;
  color: #666;
  background: #eee;
  border-radius: 3px;
  vertical-align: middle;
}
.rf-drop-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
}
.rf-file-card {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #f0d9d4;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(154, 11, 0, 0.06);
}
.rf-file-ico {
  font-size: 34px;
  color: #9a0b00;
  margin-right: 12px;
}
.rf-file-info {
  flex: 1;
  min-width: 0;
}
.rf-file-name {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  // white-space: nowrap;
  // overflow: hidden;
  // text-overflow: ellipsis;
}
.rf-file-meta {
  font-size: 12px;
  color: #999;
  margin: 4px 0;
}
.rf-file-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
}
.rf-file-actions .ant-btn-link {
  color: #9a0b00;
  padding: 0;
  height: 22px;
}
.rf-progress {
  margin-top: 14px;
}
/* 盖章图片上传区 */
.rf-photo-dragger {
  margin-top: 18px;
}
.rf-photo-card {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #f0d9d4;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(154, 11, 0, 0.06);
}
.rf-photo-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #eee;
  cursor: pointer;
  flex: 0 0 64px;
  transition: transform 0.15s;
}
.rf-photo-thumb:hover {
  transform: scale(1.04);
  box-shadow: 0 2px 10px rgba(154, 11, 0, 0.2);
}
.rf-photo-info {
  flex: 1;
  min-width: 0;
  margin: 0 10px;
}
.rf-photo-name {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rf-photo-hint {
  font-size: 12px;
  color: #b25d24;
  margin-top: 4px;
}
.rf-tips {
  margin-top: 16px;
  font-size: 12px;
  color: #b25d24;
  background: #fff7ee;
  border: 1px solid #ffe2c4;
  border-radius: 8px;
  padding: 10px 12px;
  line-height: 1.6;
}
/* 右侧预览 */
.rf-preview-panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.rf-preview-bar {
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  border-bottom: 1px solid #f0e6e2;
  background: #fcf6f4;
}
.rf-preview-title {
  font-size: 15px;
  font-weight: bold;
  color: #9a0b00;
}
.rf-preview-title .anticon {
  margin-right: 6px;
}
.rf-preview-type {
  font-size: 12px;
  color: #b25d24;
  background: #fff0e2;
  border-radius: 10px;
  padding: 2px 10px;
  white-space: nowrap;
}
.rf-preview-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rf-preview-actions .ant-btn {
  color: #9a0b00;
  border-color: #9a0b00;
}
.rf-preview-actions .ant-btn:hover {
  color: #fff;
  background: #9a0b00;
  border-color: #9a0b00;
}
.rf-preview-scroll {
  flex: 1;
  overflow: auto;
  min-height: 0;
}
.rf-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #bbb;
}
.rf-empty-icon {
  font-size: 64px;
  margin-bottom: 14px;
  color: #d9c9c4;
}
.rf-unsupported {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #888;
}
.rf-unsupported-icon {
  font-size: 60px;
  color: #9a0b00;
  margin-bottom: 14px;
}
.rf-unsupported p {
  margin-bottom: 16px;
}
.rf-loading {
  color: #9a0b00;
}
/* PDF 预览 */
.rf-pdf-wrap {
  height: 100%;
  min-height: 600px;
  background: #f5f6fa;
  padding: 16px;
}
.rf-pdf-frame {
  width: 100%;
  height: 100%;
  min-height: 0;
  border: none;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.1);
  display: block;
}
/* 全屏预览 */
.rf-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
}
.rf-fullscreen-header {
  height: 52px;
  flex: 0 0 52px;
  background: #2d2d2d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}
.rf-fullscreen-title {
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
}
.rf-fullscreen-title .anticon {
  margin-right: 8px;
  color: #ffd27a;
}
.rf-fullscreen-header .ant-btn {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.4);
  background: transparent;
}
.rf-fullscreen-header .ant-btn:hover {
  color: #fff;
  border-color: #9a0b00;
  background: #9a0b00;
}
.rf-fullscreen-frame {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  background: #333;
}
/* 图片预览弹层 */
/* 图片预览弹窗（当前页面内，不新开窗口） */
.rf-img-modal-wrap .ant-modal-body {
  padding: 12px;
  background: #1a1a1a;
}
.rf-img-modal-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: #fff;
}
.rf-img-modal-scale {
  min-width: 48px;
  text-align: center;
  font-size: 14px;
  color: #ffd27a;
  font-weight: bold;
}
.rf-img-modal-tip {
  margin-left: auto;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}
.rf-img-modal-body {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 70vh;
  overflow: hidden;
  user-select: none;
}
.rf-img-modal-img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  transform-origin: center center;
  cursor: grab;
}
</style>
